import { useAddIdPrefix } from 'hooks';
import { Avatar, Image, Typography } from 'lib/components';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import ReactDom from 'react-dom';
import { useSelectCurrentUser } from 'store';
import { Conversation, Message } from 'types';
import ChatMessages from './ChatMessages';
import classes from './index.module.scss';

interface Props {
	isOpen: boolean;
	onClose: () => any;
	conversation: Conversation;
	message: string;
	setMessage: React.Dispatch<React.SetStateAction<string>>;
	onMessageSend: () => void;
	onMessagesRead: () => void;
}

const sortMessages = (messages: Message[]): Message[] =>
	[...messages].sort((a, b) => {
		const ta = moment.utc(a.createdAt, 'MM/DD/YYYY hh:mm A').valueOf();
		const tb = moment.utc(b.createdAt, 'MM/DD/YYYY hh:mm A').valueOf();
		if (ta !== tb) {
			return ta - tb;
		}
		return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
	});

const ChatPopup = ({ isOpen, onClose, message, setMessage, onMessageSend, onMessagesRead, conversation }: Props) => {
	const addPrefix = useAddIdPrefix();
	const me = useSelectCurrentUser();

	const chatRef = React.useRef<HTMLDivElement>(null);

	const messages = useMemo(() => sortMessages(conversation.messages || []), [conversation.messages]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		onMessagesRead();
	}, [isOpen, onMessagesRead]);

	useEffect(() => {
		setTimeout(() => {
			if (chatRef.current) {
				chatRef.current.scrollTo({
					left: 0,
					top: chatRef.current.scrollHeight,
					behavior: 'smooth',
				});
			}
		}, 100);
	}, [messages, isOpen]);

	if (!isOpen) {
		return null;
	}

	return ReactDom.createPortal(
		<div className={classes['chatPopup']}>
			<div className={classes['chatPopup-top']}>
				<a onClick={onClose} className={classes['chatPopup-close']}>
					<Typography>Close Chat</Typography>
					<Image src="icCloseChat" />
				</a>
			</div>
			<div className={classes['chatPopup-actions']}>
				{conversation.nurse !== null && conversation.nurse!.phoneNumber !== undefined && (
					<a className={classes['chatPopup-action']} href={`tel:${conversation.nurse.phoneNumber}`}>
						<Image src="icTelephonePrimary" />
						<Typography weight="bold">Call</Typography>
					</a>
				)}
				{conversation.nurse !== null && conversation.nurse!.email !== undefined && (
					<a className={classes['chatPopup-action']} href={`mailto:${conversation.nurse.email}`}>
						<Image src="icMailPrimary" />
						<Typography weight="bold">Email</Typography>
					</a>
				)}
			</div>
			<div className={classes['chatPopup-userInfo']}>
				<div className={classes['chatPopup-avatar']}>
					<Avatar src={conversation.nurse?.avatar ?? 'plAvatar'} size={87} />
				</div>
				<div className={classes['chatPopup-userInfoDetail']}>
					<Typography variant="h4" weight="bold">
						{conversation.nurse?.name ?? '-'}
					</Typography>
					<Typography>{conversation.nurse!.email}</Typography>
				</div>
			</div>
			<div ref={chatRef} className={classes['chatPopup-chat']}>
				<div className={classes['chatPopup-chatPeriod']}>
					<ChatMessages messages={messages} userId={me.id} />
				</div>
			</div>
			<div className={classes['chatPopup-footer']}>
				<div className={classes['chatPopup-chatTextField']}>
					<textarea
						placeholder="Type your message"
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' && !e.shiftKey) {
								e.preventDefault();
								onMessageSend();
							}
						}}
					/>
					<Image src="icStyledSend" onClick={onMessageSend} />
				</div>
			</div>
		</div>,
		document.getElementById('chat-popup') as HTMLElement
	);
};

export default ChatPopup;
